import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/lib/api";
import { TrackedSearchPanel } from "@/pages/Profile";
import DemandMapPanel from "@/pages/admin/DemandMapPanel";

/**
 * Tracked searches tab for /admin/creators/:id.
 * Wraps the existing editor + Run now + run detail (TrackedSearchPanel) and
 * adds the Log a sighting form plus a Day 0 vs latest grid.
 */

const ENGINES = [
    { id: "chatgpt", label: "ChatGPT" },
    { id: "meta_ai_web", label: "Meta AI (web)" },
    { id: "meta_ai_instagram", label: "Meta AI (in Instagram)" },
    { id: "perplexity", label: "Perplexity" },
    { id: "google_ai_overview", label: "Google AI Overview" },
    { id: "gemini", label: "Gemini" },
    { id: "claude", label: "Claude" },
    { id: "other", label: "Other" },
];

export default function TrackedSearchesTab({ creator }) {
    const { request } = useApi();
    const [sightings, setSightings] = useState([]);
    const [sightingsLoading, setSightingsLoading] = useState(true);
    // Remounts the tracked set editor after a demand theme is added to it.
    const [panelKey, setPanelKey] = useState(0);

    const load = useCallback(async () => {
        if (!creator?.id) return;
        setSightingsLoading(true);
        try {
            const res = await request(
                `/admin/creators/${creator.id}/sightings`,
            );
            setSightings(res?.items || []);
        } catch (err) {
            toast.error(err?.message || "Could not load sightings.");
        } finally {
            setSightingsLoading(false);
        }
    }, [creator?.id, request]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div className="flex flex-col gap-5">
            <TrackedSearchPanel
                key={panelKey}
                creatorId={creator.id}
                handle={creator.samoony_handle}
            />
            <DemandMapPanel
                creatorId={creator.id}
                onTracked={() => setPanelKey((k) => k + 1)}
            />
            <LogSightingCard
                creatorId={creator.id}
                onCreated={load}
            />
            <SightingsList
                sightings={sightings}
                loading={sightingsLoading}
                onDelete={async (id) => {
                    try {
                        await request(
                            `/admin/creators/${creator.id}/sightings/${id}`,
                            { method: "DELETE" },
                        );
                        toast.success("Sighting removed.");
                        await load();
                    } catch (err) {
                        toast.error(err?.message || "Could not remove.");
                    }
                }}
            />
            <Day0VsLatest sightings={sightings} />
        </div>
    );
}

function LogSightingCard({ creatorId, onCreated }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState("");
    const [engine, setEngine] = useState("");
    const [question, setQuestion] = useState("");
    const [rank, setRank] = useState("");
    const [sources, setSources] = useState("");
    const [date, setDate] = useState("");
    const [incognito, setIncognito] = useState(true);
    const [baseline, setBaseline] = useState(false);
    const [screenshot, setScreenshot] = useState("");

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setScreenshot("");
            return;
        }
        if (file.size > 300_000) {
            toast.error("Image is too large. Use a thumbnail under 300 KB.");
            e.target.value = "";
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setScreenshot(String(reader.result || ""));
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (!result || !engine || !question.trim()) {
            toast.error("Pick result, engine and type a question.");
            return;
        }
        if (result === "named" && !rank.trim()) {
            toast.error("Add a rank or 'listed'.");
            return;
        }
        setBusy(true);
        try {
            await request(`/admin/creators/${creatorId}/sightings`, {
                method: "POST",
                body: {
                    engine,
                    question: question.trim(),
                    result,
                    rank: result === "named" ? rank.trim() : null,
                    cited_sources: sources.trim() || null,
                    incognito,
                    baseline,
                    date: date || null,
                    screenshot_data_url: screenshot || null,
                },
            });
            toast.success("Sighting logged.");
            setResult("");
            setEngine("");
            setQuestion("");
            setRank("");
            setSources("");
            setDate("");
            setIncognito(true);
            setBaseline(false);
            setScreenshot("");
            setOpen(false);
            onCreated?.();
        } catch (err) {
            toast.error(err?.message || "Could not log sighting.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <div
                data-testid="log-sighting-card"
                className="bg-white border border-black/10 rounded-2xl p-5"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="samoony-label">Log a sighting</div>
                        <p className="text-sm text-black/60 mt-1">
                            Record a check you ran yourself. Named sightings
                            count toward the Named status chip.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="log-sighting-open"
                        onClick={() => setOpen(true)}
                        className="samoony-chip"
                    >
                        Log one
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form
            data-testid="log-sighting-form"
            onSubmit={submit}
            className="bg-white border border-black/10 rounded-2xl p-5 flex flex-col gap-3"
        >
            <div className="flex items-center justify-between">
                <div className="samoony-label">Log a sighting</div>
                <button
                    type="button"
                    data-testid="log-sighting-cancel"
                    onClick={() => setOpen(false)}
                    className="text-xs text-black/50 hover:text-ink"
                >
                    Cancel
                </button>
            </div>
            <label className="text-xs text-black/60">
                Result
                <select
                    data-testid="log-sighting-result"
                    value={result}
                    onChange={(e) => setResult(e.target.value)}
                    className="samoony-input !h-9 w-full"
                >
                    <option value="">Pick…</option>
                    <option value="named">Named</option>
                    <option value="not_named">Checked, not named</option>
                </select>
            </label>
            {result === "named" && (
                <label className="text-xs text-black/60">
                    Rank (number or 'listed')
                    <input
                        data-testid="log-sighting-rank"
                        value={rank}
                        onChange={(e) => setRank(e.target.value)}
                        placeholder="e.g. 9 or listed"
                        className="samoony-input !h-9 w-full"
                    />
                </label>
            )}
            <label className="text-xs text-black/60">
                Engine
                <select
                    data-testid="log-sighting-engine"
                    value={engine}
                    onChange={(e) => setEngine(e.target.value)}
                    className="samoony-input !h-9 w-full"
                >
                    <option value="">Pick…</option>
                    {ENGINES.map((e) => (
                        <option key={e.id} value={e.id}>
                            {e.label}
                        </option>
                    ))}
                </select>
            </label>
            <label className="text-xs text-black/60">
                Exact question
                <textarea
                    data-testid="log-sighting-question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={2}
                    className="samoony-input w-full"
                    placeholder="Paste the prompt you used"
                />
            </label>
            <label className="text-xs text-black/60">
                Cited sources (optional)
                <input
                    data-testid="log-sighting-sources"
                    value={sources}
                    onChange={(e) => setSources(e.target.value)}
                    placeholder="Comma-separated URLs or site names"
                    className="samoony-input !h-9 w-full"
                />
            </label>
            <label className="text-xs text-black/60">
                Date (defaults to today Edmonton)
                <input
                    data-testid="log-sighting-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="samoony-input !h-9 w-full"
                />
            </label>
            <label className="text-xs text-black/60 flex items-center gap-2">
                <input
                    data-testid="log-sighting-incognito"
                    type="checkbox"
                    checked={incognito}
                    onChange={(e) => setIncognito(e.target.checked)}
                />
                Checked in incognito / signed-out mode
            </label>
            <label className="text-xs text-black/60 flex items-center gap-2">
                <input
                    data-testid="log-sighting-baseline"
                    type="checkbox"
                    checked={baseline}
                    onChange={(e) => setBaseline(e.target.checked)}
                />
                Baseline (Day 0 entry)
            </label>
            <label className="text-xs text-black/60">
                Screenshot (optional, under 300 KB)
                <input
                    data-testid="log-sighting-screenshot"
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="block mt-1 text-xs"
                />
            </label>
            <div className="flex justify-end">
                <button
                    type="submit"
                    data-testid="log-sighting-submit"
                    disabled={busy}
                    className="samoony-chip disabled:opacity-30"
                >
                    {busy ? "Logging..." : "Log sighting"}
                </button>
            </div>
        </form>
    );
}

function SightingsList({ sightings, loading, onDelete }) {
    if (loading) {
        return (
            <div className="text-sm text-black/50">Loading sightings...</div>
        );
    }
    if (!sightings.length) {
        return (
            <div
                data-testid="sightings-empty"
                className="bg-white border border-black/10 rounded-2xl p-5 text-sm text-black/50"
            >
                No sightings logged yet.
            </div>
        );
    }
    return (
        <section
            data-testid="sightings-list"
            className="bg-white border border-black/10 rounded-2xl"
        >
            <div className="px-5 py-3 border-b border-black/5 samoony-label">
                Logged sightings ({sightings.length})
            </div>
            <ul className="divide-y divide-black/5">
                {sightings.map((s) => {
                    const eng = ENGINES.find((e) => e.id === s.engine);
                    return (
                        <li
                            key={s.id}
                            data-testid={`sighting-row-${s.id}`}
                            className="px-5 py-3"
                        >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider ${s.result === "named" ? "bg-signal/15 text-signal" : "bg-black/5 text-black/60"}`}
                                    >
                                        {s.result === "named"
                                            ? `Named${s.rank ? ` · ${s.rank}` : ""}`
                                            : "Not named"}
                                    </span>
                                    <span className="font-mono text-[11px] text-black/60">
                                        {eng?.label || s.engine}
                                    </span>
                                    <span className="font-mono text-[11px] text-black/50">
                                        {s.date}
                                    </span>
                                    {s.baseline && (
                                        <span className="font-mono text-[11px] uppercase tracking-wider text-electric">
                                            Baseline
                                        </span>
                                    )}
                                    {s.incognito && (
                                        <span className="font-mono text-[11px] text-black/40">
                                            incognito
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`sighting-delete-${s.id}`}
                                    onClick={() => {
                                        if (window.confirm("Remove this sighting?"))
                                            onDelete(s.id);
                                    }}
                                    className="text-xs text-black/40 hover:text-ink"
                                >
                                    Remove
                                </button>
                            </div>
                            <div className="mt-1 text-sm text-ink">
                                {s.question}
                            </div>
                            {s.cited_sources && (
                                <div className="mt-1 text-xs text-black/50 break-all">
                                    Sources: {s.cited_sources}
                                </div>
                            )}
                            {s.screenshot_data_url && (
                                <img
                                    src={s.screenshot_data_url}
                                    alt="Sighting screenshot"
                                    className="mt-2 max-h-40 rounded-md border border-black/10"
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

// Day 0 vs latest: for each (question + engine) key, show the baseline
// entry next to the most recent non-baseline check so staff can see change.
function Day0VsLatest({ sightings }) {
    const rows = useMemo(() => {
        const byKey = new Map();
        for (const s of sightings) {
            const key = `${s.engine}::${s.question.trim().toLowerCase()}`;
            const entry = byKey.get(key) || {
                engine: s.engine,
                question: s.question,
                baseline: null,
                latest: null,
            };
            if (s.baseline) {
                if (!entry.baseline || s.date < entry.baseline.date)
                    entry.baseline = s;
            } else {
                if (!entry.latest || s.date > entry.latest.date)
                    entry.latest = s;
            }
            byKey.set(key, entry);
        }
        return Array.from(byKey.values()).filter(
            (r) => r.baseline || r.latest,
        );
    }, [sightings]);

    if (!rows.length) return null;

    return (
        <section
            data-testid="day0-vs-latest"
            className="bg-white border border-black/10 rounded-2xl"
        >
            <div className="px-5 py-3 border-b border-black/5 samoony-label">
                Day 0 vs latest
            </div>
            <ul className="divide-y divide-black/5">
                {rows.map((r, i) => {
                    const eng = ENGINES.find((e) => e.id === r.engine);
                    return (
                        <li
                            key={i}
                            data-testid={`day0-row-${i}`}
                            className="px-5 py-3"
                        >
                            <div className="text-sm">
                                <strong className="font-medium">
                                    {eng?.label || r.engine}
                                </strong>{" "}
                                · {r.question}
                            </div>
                            <div className="mt-1 grid grid-cols-2 gap-3 text-xs">
                                <ResultCell
                                    label="Day 0"
                                    s={r.baseline}
                                    empty="No baseline logged"
                                />
                                <ResultCell
                                    label="Latest"
                                    s={r.latest}
                                    empty="No recent check"
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function ResultCell({ label, s, empty }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-black/40">
                {label}
            </div>
            {s ? (
                <div className="text-ink">
                    {s.result === "named"
                        ? `Named${s.rank ? ` · ${s.rank}` : ""}`
                        : "Not named"}
                    <div className="font-mono text-[11px] text-black/40">
                        {s.date}
                    </div>
                </div>
            ) : (
                <div className="text-black/40">{empty}</div>
            )}
        </div>
    );
}
